import { Button, Icon, Spinner, Typography } from '@scale/scaleui';
import { useEffect, useState } from 'react';
import {
  downloadUsersOnStepCSV,
  useFetchPaginatedUsersOnStep,
} from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import PanelEntry from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/PanelEntry';
import { toast } from 'react-toastify';
import {
  OnboardingFlowNode,
  OnboardingFlowNodeType,
  OnboardingFlowType,
  SimpleStepStatsResponse,
  ParseDuration,
} from '@scale/shared/onboardingControlPanel';
import CopyToClipboard from 'react-copy-to-clipboard';

interface User {
  user_id: string;
  email?: string;
  duration?: string;
}

interface UsersOnStepProps {
  flowId: string;
  flowName: string;
  node: OnboardingFlowNode;
  activeUsersData: SimpleStepStatsResponse | number | undefined;
}
const UsersOnStep: React.FC<UsersOnStepProps> = ({ flowId, flowName, node, activeUsersData }) => {
  const [durationSortOrder, setDurationSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [users, setUsers] = useState<User[]>([]);
  const [isCSVLoading, setIsCSVLoading] = useState<boolean>(false);

  const totalUsers =
    typeof activeUsersData === 'number' ? activeUsersData : activeUsersData?.result || 0;
  const pageSize = 10;

  const {
    isLoading: isLoadingPaginatedUsers,
    data: paginedUsersData,
    error: loadingPaginatedUsersError,
  } = useFetchPaginatedUsersOnStep(flowId, node.name, currentPage, pageSize, durationSortOrder);

  useEffect(() => {
    if (!isLoadingPaginatedUsers && paginedUsersData) {
      paginedUsersData.map(user => {
        if (user.duration) {
          const formattedDuration = ParseDuration(user.duration);
          user.duration = formattedDuration;
        }
      });
      setUsers(paginedUsersData);
    }
  }, [isLoadingPaginatedUsers, paginedUsersData]);

  // TODO: separate this out so growth and ACP don't share the same endpoint
  async function handleDownloadUsersOnStepCSV() {
    setIsCSVLoading(true);
    await downloadUsersOnStepCSV(flowId, flowName, node.name, OnboardingFlowType.Growth);
    setIsCSVLoading(false);
  }
  const notifyCopySuccess = () =>
    toast('Copied!', {
      position: 'bottom-center',
      autoClose: 1000,
      progressStyle: { background: 'green' },
    });

  useEffect(() => {
    setCurrentPage(1);
  }, [node.name]);

  const handleNext = () => {
    setCurrentPage(currentPage + 1);
  };

  const handlePrevious = () => {
    setCurrentPage(currentPage - 1);
  };

  const toggleDurationSortOrder = () => {
    setDurationSortOrder(durationSortOrder === 'asc' ? 'desc' : 'asc');
    setCurrentPage(1);
  };

  return (
    <div>
      <PanelEntry
        title="Users currently on this step"
        titleButton={
          <div className="flex justify-between items-center">
            {isCSVLoading ? (
              <>
                <Typography.Paragraph3 className="mr-1">Downloading...</Typography.Paragraph3>
                <Spinner size="xs" />
              </>
            ) : (
              <Icon
                icon="download"
                onClick={() => handleDownloadUsersOnStepCSV()}
                tooltip="Download CSV"
              />
            )}
          </div>
        }
      >
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center">Tasker Email + ID</div>
          {node.type === OnboardingFlowNodeType.Step && (
            <div
              className="flex items-center align- cursor-pointer"
              onClick={() => toggleDurationSortOrder()}
            >
              <div>Duration</div>
              <Icon
                className={`${durationSortOrder === 'desc' ? '' : 'rotate-180'}`}
                icon="chevron-down"
                hasBackground={false}
                variant="neutral"
                size="sm"
              />
            </div>
          )}
        </div>

        {isLoadingPaginatedUsers ? (
          <p>Loading users...</p>
        ) : (
          <div>
            {loadingPaginatedUsersError ? (
              <p>Error loading users</p>
            ) : users.length === 0 ? (
              <p>No users on this step</p>
            ) : (
              users.map(user => (
                <div
                  key={user.user_id}
                  className="grid grid-cols-3 gap-4 items-center mb-4 pb-4 border-b border-neutral-200"
                >
                  <div className="col-span-2 flex flex-col justify-between">
                    <div className="flex items-center space-x-1">
                      <Typography.Paragraph3 className="truncate">
                        {user.email}
                      </Typography.Paragraph3>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Typography.Paragraph3>ID:</Typography.Paragraph3>
                      <a
                        href={`${window.location.origin}/corp/lookup/${user.user_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'underline', marginRight: '8px' }}
                      >
                        <Typography.Paragraph3 className="truncate">
                          {user.user_id}
                        </Typography.Paragraph3>
                      </a>
                      <CopyToClipboard text={user.user_id} onCopy={notifyCopySuccess}>
                        <Icon icon="copy" hasBackground={false} variant="neutral" size="sm" />
                      </CopyToClipboard>
                    </div>
                  </div>
                  {node.type === OnboardingFlowNodeType.Step && (
                    <div className="text-right self-center">
                      <Typography.Paragraph3>{user.duration ?? 'N/A'}</Typography.Paragraph3>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
        <div className="flex justify-center items-center space-x-2 mt-2 mb-20">
          <Button onClick={handlePrevious} disabled={currentPage <= 1}>
            Previous
          </Button>
          <span>
            Page {currentPage} of {Math.max(1, Math.ceil(totalUsers / pageSize))}
          </span>
          <Button onClick={handleNext} disabled={currentPage * pageSize >= totalUsers}>
            Next
          </Button>
        </div>
      </PanelEntry>
    </div>
  );
};

export default UsersOnStep;
